import React, { useState } from 'react';
import { addressAPI } from '../services/api';

export interface AddressFields {
  addressLine1: string;
  addressLine2: string;
  city: string;
  county: string;
  postcode: string;
  /** Organisation/business name returned by PAF — use as site name if blank */
  organisation: string;
}

interface Props {
  /** Called when user selects an address from the dropdown */
  onSelect: (address: AddressFields) => void;
  /** Optional placeholder for the postcode input */
  placeholder?: string;
}

/**
 * Reusable postcode search widget (VDGL AddressDetails package).
 * Enter a postcode → Search → pick from dropdown → address fields populated.
 */
const PostcodeLookupInput: React.FC<Props> = ({ onSelect, placeholder = 'e.g. LS19 7YA' }) => {
  const [postcode, setPostcode]       = useState('');
  const [results, setResults]         = useState<AddressFields[]>([]);
  const [summaries, setSummaries]     = useState<string[]>([]);
  const [loading, setLoading]         = useState(false);
  const [searched, setSearched]       = useState(false);
  const [error, setError]             = useState('');

  const handleSearch = async () => {
    const pc = postcode.trim();
    if (!pc) { setError('Please enter a postcode.'); return; }
    setError('');
    setLoading(true);
    setSearched(false);
    setResults([]);
    setSummaries([]);
    try {
      const res = await addressAPI.lookup(pc);
      const data = res.data;
      if (!data || data.length === 0) {
        setError('No addresses found for that postcode. Please check and try again.');
      } else {
        setResults(data.map(a => ({
          addressLine1: a.addressLine1,
          addressLine2: a.addressLine2,
          city:         a.city,
          county:       a.county,
          postcode:     a.postcode,
          organisation: a.organisation,
        })));
        setSummaries(data.map(a => a.summaryAddress));
      }
    } catch {
      setError('Postcode lookup failed. Please enter the address manually.');
    } finally {
      setLoading(false);
      setSearched(true);
    }
  };

  const handleSelect = (idx: number) => {
    onSelect(results[idx]);
    setResults([]);
    setSummaries([]);
    setSearched(false);
    setPostcode('');
  };

  return (
    <div style={{ marginBottom: 16 }}>
      {/* Input row */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <div style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          border: '2px solid #3b82f6',
          borderRadius: 8,
          overflow: 'hidden',
          background: '#fff',
        }}>
          {/* Blue flag strip (matches plate style) */}
          <div style={{
            background: '#002060', width: 36, display: 'flex',
            flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            padding: '6px 0', flexShrink: 0,
          }}>
            <span style={{ color: '#FFA500', fontSize: 9, fontWeight: 700, letterSpacing: 1 }}>UK</span>
            <span style={{ fontSize: 14 }}>🏠</span>
          </div>
          <input
            type="text"
            value={postcode}
            onChange={e => setPostcode(e.target.value.toUpperCase())}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            placeholder={placeholder}
            style={{
              flex: 1, border: 'none', outline: 'none',
              padding: '10px 12px', fontSize: 15, fontWeight: 700,
              letterSpacing: 2, fontFamily: 'monospace', background: 'transparent',
            }}
          />
        </div>
        <button
          type="button"
          className="btn btn-secondary"
          onClick={handleSearch}
          disabled={loading || !postcode.trim()}
          style={{ whiteSpace: 'nowrap', fontSize: 13 }}
        >
          {loading ? 'Searching…' : 'Search Postcode'}
        </button>
      </div>

      {/* Error */}
      {error && (
        <div style={{ marginTop: 6, fontSize: 13, color: '#b91c1c', padding: '6px 10px', background: '#fef2f2', borderRadius: 6 }}>
          {error}
        </div>
      )}

      {/* Dropdown results */}
      {summaries.length > 0 && (
        <div style={{
          marginTop: 6, border: '1px solid #bae6fd', borderRadius: 8,
          background: '#fff', boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          maxHeight: 260, overflowY: 'auto',
        }}>
          <div style={{ padding: '8px 12px', fontSize: 12, color: '#6b7280', borderBottom: '1px solid #e5e7eb', fontWeight: 600 }}>
            {summaries.length} address{summaries.length !== 1 ? 'es' : ''} found — select to confirm
          </div>
          {summaries.map((s, i) => (
            <button
              key={i}
              type="button"
              onClick={() => handleSelect(i)}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                padding: '10px 14px', border: 'none', background: 'none',
                cursor: 'pointer', fontSize: 13, color: '#1e293b',
                borderBottom: i < summaries.length - 1 ? '1px solid #f1f5f9' : 'none',
              }}
              onMouseEnter={e => (e.currentTarget.style.background = '#f0f9ff')}
              onMouseLeave={e => (e.currentTarget.style.background = 'none')}
            >
              {s}
            </button>
          ))}
        </div>
      )}

      {/* Confirmed / empty state */}
      {searched && summaries.length === 0 && !error && (
        <div style={{ marginTop: 6, fontSize: 13, color: '#6b7280' }}>
          No results — please enter the address manually below.
        </div>
      )}
    </div>
  );
};

export default PostcodeLookupInput;
